import { Link as ReachLink } from '@reach/router'
import { ReactNode, useRef } from 'react'
import { BoxProps, Skeleton } from '~/design_system'
import { useMsg } from '../../../common/localizationUtils'
import { useIsWithinReachRouter } from '../../../common/reachRouterUtils'
import { SadStates } from '../../../components/SadStates'
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Flex,
  Link as ChakraLink,
  LinkProps,
  Text,
  Stack
} from '../../chakra'
import { CardHeading } from '../cards/Cards'
import { NoResults, RefreshError } from '../empty_states/EmptyStates'
import { downloadHtmlNodeAsImage } from '../../../dashboards/utils'
import { useDashboardPermissions } from '../../../dashboards/hooks'

export const useWidgetReportUrl = () => {
  const url = '/reporting/new'
  return { url }
}

type FloatingReportLinkProps = Omit<LinkProps, 'href' | 'to' | 'state'> & {
  reportKey: string
  title: string
  children: ReactNode
}

const FloatingReportLink = ({ reportKey, title, children, ...props }: FloatingReportLinkProps) => {
  const { url: widgetReportUrl } = useWidgetReportUrl()
  const url = `${widgetReportUrl}?&key=${reportKey}`
  const isReachRouter = useIsWithinReachRouter()
  return (
    <ChakraLink
      {...(isReachRouter && { as: ReachLink })}
      to={url}
      href={url}
      target="_blank"
      state={{ reportKey, title }}
      {...props}>
      {children}
    </ChakraLink>
  )
}

type WidgetReportLinkProps = {
  reportKey: string
  title: string
  reportTitleId: string
}

const WidgetReportLink = ({ reportKey, title, reportTitleId }: WidgetReportLinkProps) => {
  const msg = useMsg()

  return (
    <FloatingReportLink
      aria-describedby={reportTitleId}
      target="_blank"
      reportKey={reportKey}
      title={title}
      display="inline-flex"
      alignItems="right">
      <>
        <span>{msg('dashboards.chart.view_report.link')}</span>
      </>
    </FloatingReportLink>
  )
}

const SubTitleWrapper = ({ children }: { children: ReactNode }) => (
  <Text fontWeight={400} lineHeight="24px" fontSize="14px">
    {children}
  </Text>
)

type CustomReportLinkProps = {
  url: string
  reportTitleId: string
}

const CustomReportLink = ({ url, reportTitleId }: CustomReportLinkProps) => {
  const msg = useMsg()
  return (
    <ChakraLink
      aria-describedby={reportTitleId}
      target="_blank"
      color="brand.500"
      href={url}
      display="inline-flex"
      alignItems="right"
      textAlign="right">
      <span>{msg('dashboards.chart.view_report.link')}</span>
    </ChakraLink>
  )
}

export type ChartProps = {
  children: ReactNode
  title: string
  subTitle?: string
  isLoading?: boolean
  isError?: boolean
  isFetching?: boolean
  isEmpty?: boolean
  noResultsHeading?: string
  reportKey?: string
  customReportUrl?: string
  cardStyle?: BoxProps
}

export const ChartContainer = ({
  children,
  title,
  subTitle,
  customReportUrl = '',
  reportKey,
  isLoading = true,
  isError = false,
  isFetching = true,
  isEmpty = false,
  noResultsHeading
}: ChartProps) => {
  const { has_access_to_custom_report_templates: hasAccessToReportTemplates, can_export_custom_reports: canExport } =
    useDashboardPermissions() || {}
  const msg = useMsg()

  const containerRef = useRef(null)

  const isValidChart = !isError && !isEmpty
  const showSubtitle = isValidChart && !isLoading && subTitle
  const showInlineFetching = !isLoading && isFetching
  const showCustomReportUrl = !isError && hasAccessToReportTemplates && (customReportUrl || reportKey)
  const reportTitleId = `card-title-${reportKey || customReportUrl}`
  const ReportLink = () =>
    reportKey ? (
      <WidgetReportLink reportKey={reportKey} title={title} reportTitleId={reportTitleId} />
    ) : (
      <CustomReportLink url={customReportUrl} reportTitleId={reportTitleId} />
    )

  return (
    <Card data-testid={`chart-widget-${title}`}>
      <CardHeader flexGrow={1} justifyContent="space-between">
        <CardHeading as={Flex} maxWidth="356px" direction={{ base: 'column', lg: 'row' }}>
          <Stack>
            <Text id={reportTitleId}>{title}</Text>
            {showSubtitle && <SubTitleWrapper>{subTitle}</SubTitleWrapper>}
          </Stack>
        </CardHeading>
        {isValidChart && canExport && (
          <Button
            minWidth="125px"
            aria-describedby={reportTitleId}
            variant="secondary"
            onClick={() => {
              downloadHtmlNodeAsImage(containerRef.current, title)
            }}>
            {msg('dashboards.chart.download.label')}
          </Button>
        )}
      </CardHeader>
      <CardBody
        backgroundColor="white"
        ref={containerRef}
        height={400}
        padding={4}
        flex="none"
        position="relative"
        opacity={showInlineFetching ? 0.5 : 1}>
        <SadStates
          states={[
            {
              when: isLoading,
              render: () => <Skeleton width="100%" height="50%" />
            },
            {
              when: isError,
              render: () => (
                <Flex direction="column" height="100%" justifyContent="center">
                  <RefreshError
                    title={msg('common.ui.error_processing_request_try_again')}
                    graphicHeight="50%"
                    textAlign="center"
                  />
                </Flex>
              )
            },
            {
              when: isEmpty,
              render: () => (
                <Flex direction="column" height="100%" justifyContent="center">
                  <NoResults
                    title={noResultsHeading || msg('common.ui.no_results_found')}
                    graphicHeight="50%"
                    textAlign="center"
                  />
                  <Text textStyle="bodySemi" textAlign="center">
                    {msg('common.ui.adjust_filters')}
                  </Text>
                </Flex>
              )
            }
          ]}>
          {children}
        </SadStates>
      </CardBody>
      {showCustomReportUrl && (
        <CardFooter justifyContent="right">
          <ReportLink />
        </CardFooter>
      )}
    </Card>
  )
}
